import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { BRAND } from '../../lib/theme';
import { useAuth } from '../../contexts/AuthContext';
import { formatDate } from '../../lib/dateFormat';

export interface BackupCodesDisplayProps {
  codes: string[];
  onDone: () => void;
}

/**
 * Displays 10 backup codes in a 2×5 grid.
 *
 * Security constraints (ADR-0003 §8, UX-spec §8):
 * - Codes are NEVER stored in localStorage/sessionStorage.
 * - "Done" button is disabled until the acknowledge checkbox is checked.
 * - On Done: codes are cleared from memory and navigation proceeds.
 * - Print-CSS block renders separately so normal UI can hide it.
 */
export function BackupCodesDisplay({ codes, onDone }: BackupCodesDisplayProps) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [acknowledged, setAcknowledged] = useState(false);
  const [copyToast, setCopyToast] = useState(false);

  const handleCopyAll = useCallback(() => {
    const text = [
      t('mfa.setup.backup.export.app_name'),
      t('mfa.setup.backup.export.account', { email: user?.email ?? '' }),
      t('mfa.setup.backup.export.date', { date: formatDate(new Date()) }),
      '',
      ...codes,
      '',
      t('mfa.setup.backup.export.store_safely'),
      t('mfa.setup.backup.export.single_use'),
    ].join('\n');

    navigator.clipboard.writeText(text).then(() => {
      setCopyToast(true);
      setTimeout(() => setCopyToast(false), 2500);
    });
  }, [codes, user?.email, t]);

  const handleDownload = useCallback(() => {
    const text = [
      t('mfa.setup.backup.export.app_name'),
      t('mfa.setup.backup.export.account', { email: user?.email ?? '' }),
      t('mfa.setup.backup.export.date', { date: formatDate(new Date()) }),
      '',
      ...codes.map((c, i) => `${i + 1}. ${c}`),
      '',
      t('mfa.setup.backup.export.store_safely'),
      t('mfa.setup.backup.export.single_use'),
    ].join('\n');

    const blob = new Blob([text], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'senihelp24-backup-codes.txt';
    a.click();
    URL.revokeObjectURL(url);
  }, [codes, user?.email, t]);

  const handlePrint = () => window.print();

  const handleDone = () => {
    if (!acknowledged) return;
    onDone();
  };

  return (
    <>
      {/* ── Screen view ── */}
      <div style={{ maxWidth: 480 }}>
        <h2 style={{ fontSize: 20, fontWeight: 700, color: '#101828', marginBottom: 8 }}>
          {t('mfa.setup.backup.title')}
        </h2>
        <p style={{ fontSize: 14, color: '#667085', marginBottom: 20, lineHeight: 1.6 }}>
          {t('mfa.setup.backup.intro')}
        </p>

        {/* Code grid */}
        <div
          role="list"
          aria-label={t('mfa.setup.backup.codes_list_aria', { count: codes.length })}
          style={{
            display: 'grid',
            gridTemplateColumns: '1fr 1fr',
            gap: 10,
            marginBottom: 20,
          }}
        >
          {codes.map((code, i) => (
            <code
              key={i}
              role="listitem"
              style={{
                display: 'block',
                fontFamily: 'monospace',
                fontSize: 15,
                fontWeight: 600,
                padding: '10px 12px',
                background: '#f9fafb',
                border: '1px solid #e4e7ec',
                borderRadius: 8,
                textAlign: 'center',
                userSelect: 'all',
                letterSpacing: 2,
              }}
            >
              {code}
            </code>
          ))}
        </div>

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 8, marginBottom: 24, flexWrap: 'wrap' }}>
          <button
            type="button"
            onClick={handleCopyAll}
            style={btnStyle}
            aria-live="polite"
          >
            {copyToast ? '✓ ' + t('mfa.setup.backup.toast_copied') : t('mfa.setup.backup.action.copy_all')}
          </button>
          <button type="button" onClick={handleDownload} style={btnStyle}>
            {t('mfa.setup.backup.action.download')}
          </button>
          <button type="button" onClick={handlePrint} style={btnStyle}>
            {t('mfa.setup.backup.action.print')}
          </button>
        </div>

        {/* Acknowledge checkbox */}
        <label
          htmlFor="backup-codes-ack"
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            cursor: 'pointer',
            marginBottom: 24,
          }}
        >
          <input
            id="backup-codes-ack"
            type="checkbox"
            checked={acknowledged}
            onChange={e => setAcknowledged(e.target.checked)}
            style={{ width: 16, height: 16, cursor: 'pointer', accentColor: BRAND }}
          />
          <span style={{ fontSize: 13, color: '#344054' }}>
            {t('mfa.setup.backup.acknowledge_label')}
          </span>
        </label>

        <button
          type="button"
          onClick={handleDone}
          disabled={!acknowledged}
          aria-disabled={!acknowledged}
          style={{
            width: '100%',
            background: acknowledged ? BRAND : '#d0d5dd',
            color: '#fff',
            border: 'none',
            borderRadius: 9,
            padding: '11px',
            fontSize: 14,
            fontWeight: 600,
            cursor: acknowledged ? 'pointer' : 'not-allowed',
            fontFamily: 'Outfit, sans-serif',
            opacity: acknowledged ? 1 : 0.65,
          }}
        >
          {t('mfa.setup.backup.done')}
        </button>
      </div>

      {/* ── Print-only block (hidden on screen, visible when printing) ── */}
      <div
        className="print-codes"
        style={{ display: 'none' }}
        aria-hidden="true"
      >
        <style>{printStyles}</style>
        <h1>{t('mfa.setup.backup.export.app_name')}</h1>
        <p>{t('mfa.setup.backup.export.account', { email: user?.email ?? '' })} | {t('mfa.setup.backup.export.date', { date: formatDate(new Date()) })}</p>
        <ol>
          {codes.map((code, i) => <li key={i}>{code}</li>)}
        </ol>
        <p className="note">
          {t('mfa.setup.backup.export.store_safely')}
          {' '}
          {t('mfa.setup.backup.export.single_use')}
        </p>
      </div>
    </>
  );
}

const btnStyle: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 13,
  fontWeight: 500,
  border: '1px solid #e4e7ec',
  borderRadius: 8,
  background: '#fff',
  color: '#344054',
  cursor: 'pointer',
};

const printStyles = `
@media print {
  body * { visibility: hidden; }
  .print-codes, .print-codes * { visibility: visible; }
  .print-codes {
    display: block !important;
    position: absolute; top: 0; left: 0;
    width: 100%; padding: 2cm; font-family: monospace;
  }
  .print-codes h1 { font-size: 18pt; margin-bottom: 0.5cm; }
  .print-codes ol { font-size: 14pt; line-height: 2; }
  .print-codes .note { font-size: 10pt; color: #666; margin-top: 1cm; }
}
`;
